import { polygon, point } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Coordinate } from 'ol/coordinate';
import { Mission, Sample } from '../db';
import { alertConfirm } from '../alertDispatcher';
import { promptForChangeReason, ShowChangeTypeSelectorPopup } from '../changeReasonHelpers';

/**
 * Singleton service for skipping and unskipping samples inside polygons
 */
export class SampleSkipper {
  private static instance: SampleSkipper;

  /**
   * Private constructor to prevent direct instantiation
   */
  private constructor() {
    // Prevent instantiation
    if (SampleSkipper.instance) {
      throw new Error('SampleSkipper is a singleton. Use SampleSkipper.getInstance() instead.');
    }
  }

  /**
   * Get the singleton instance of SampleSkipper
   */
  public static getInstance(): SampleSkipper {
    if (!SampleSkipper.instance) {
      SampleSkipper.instance = new SampleSkipper();
    }

    return SampleSkipper.instance;
  }

  /**
   * Skip samples that are inside a polygon
   * @param mission The mission containing samples to skip
   * @param polygonPointsLatLon Array of polygon rings with coordinates in [lat, lon] format
   */
  public async skipSamplesInsidePolygon(
    mission: Mission | undefined,
    polygonPointsLatLon: Coordinate[][],
  ): Promise<void> {
    if (!mission) {
      return;
    }

    // turf needs "lon, lat", we receive "lat, lon"
    const skipPolygon = polygon(polygonPointsLatLon.map((ring) => ring.map((coordSet) => [coordSet[1], coordSet[0]])));

    if (mission.is_zone_mission()) {
      return;
    }

    const skipCandidates: Sample[] = mission.getAllSamples().filter((sample) => {
      if (sample.hasBarcode() || sample.skipped_or_deleted) {
        return false;
      }

      const centroid = sample.getSampleSite().getSampleCentroid();

      if (!centroid) {
        return false;
      }

      const pt = point([centroid.lon, centroid.lat]);

      if (!booleanPointInPolygon(pt, skipPolygon)) {
        return false;
      }

      return true;
    });

    const samplesToSkipNumber = skipCandidates.length;

    if (!samplesToSkipNumber) {
      return;
    }

    if (!(await alertConfirm(`Are you sure you want to skip ${samplesToSkipNumber} samples?`))) {
      return;
    }

    const selectorOptions: ShowChangeTypeSelectorPopup = {
      sampleCoreOrZoneId: `${samplesToSkipNumber} SAMPLES`,
      changeTypeOptions: ['Skip', 'Delete', 'None'],
      defaultChangeType: 'Skip',
    };

    const changeSampleResult = await promptForChangeReason(selectorOptions);

    if (changeSampleResult.result === 'confirm' && !!changeSampleResult.selectedChangeType) {
      await mission.skipMultipleSamples(
        skipCandidates,
        changeSampleResult.selectedChangeType,
        changeSampleResult.reason ?? '',
      );
    }
  }

  /**
   * Unskip samples that are inside a polygon (reverse of skip operation)
   * @param mission The mission containing samples to unskip
   * @param polygonPointsLatLon Array of polygon rings with coordinates in [lat, lon] format
   */
  public async unskipSamplesInsidePolygon(
    mission: Mission | undefined,
    polygonPointsLatLon: Coordinate[][],
  ): Promise<void> {
    if (!mission) {
      return;
    }

    // turf needs "lon, lat", we receive "lat, lon"
    const skipPolygon = polygon(polygonPointsLatLon.map((ring) => ring.map((coordSet) => [coordSet[1], coordSet[0]])));

    if (mission.is_zone_mission()) {
      return;
    }

    const unskipCandidates: Sample[] = mission.getAllSamples().filter((sample) => {
      // Only consider samples that are currently skipped or deleted
      if (!sample.skipped_or_deleted) {
        return false;
      }

      const centroid = sample.getSampleSite().getSampleCentroid();

      if (!centroid) {
        return false;
      }

      const pt = point([centroid.lon, centroid.lat]);

      if (!booleanPointInPolygon(pt, skipPolygon)) {
        return false;
      }

      return true;
    });

    const samplesToUnskipNumber = unskipCandidates.length;

    if (!samplesToUnskipNumber) {
      return;
    }

    if (!(await alertConfirm(`Are you sure you want to unskip ${samplesToUnskipNumber} samples?`))) {
      return;
    }

    await mission.unskipMultipleSamples(unskipCandidates);
  }
}
